import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Mic, Sparkles, Square } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@Components/ui-v2/tooltip';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import useSpeechToText from '../../../CustomHooks/Agents/useSpeechToText';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import ConnectorPicker from '../Chrome/ConnectorPicker';

const MAX_TEXTAREA_HEIGHT = 160;

interface ComposerProps {
  onSend: (message: string) => void;
  /** Cancels in-flight generation; returns the prompt so the composer can restore it. */
  onStop?: () => string | null | void;
  onOpenJourneys?: () => void;
  isGenerating?: boolean;
  /** False while tokens stream back — disables Stop to prevent half-cancels. */
  canStop?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const Composer = ({
  onSend,
  onStop,
  onOpenJourneys,
  isGenerating = false,
  canStop = true,
  disabled = false,
  placeholder,
}: ComposerProps) => {
  const { t, i18n } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('Agent.ComposerPlaceholder');
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    isSupported: isVoiceSupported,
    isListening,
    start: startVoice,
    stop: stopVoice,
  } = useSpeechToText({
    lang: i18n.language?.startsWith('es') ? 'es-ES' : 'en-US',
    onTranscript: (text) => {
      setValue((prev) => (prev ? `${prev} ${text}` : text));
    },
  });

  const handleVoiceToggle = () => {
    if (isListening) stopVoice();
    else startVoice();
  };

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isGenerating || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleStop = () => {
    const restored = onStop?.();
    if (typeof restored === 'string' && restored) {
      setValue(restored);
      window.setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const hasContent = value.trim().length > 0;
  const isActionDisabled = isGenerating ? !canStop : !hasContent || disabled;

  return (
    <div className="border-t border-gray-200 p-4" style={{ backgroundColor: '#ffffff' }}>
      <div className="mx-auto w-full min-w-0 max-w-3xl">
        <div className="mb-2">
          <ConnectorPicker />
        </div>
        <div className="rounded-2xl border border-gray-100 bg-[#fbfaf9] px-3 pt-3 pb-2">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={resolvedPlaceholder}
            rows={1}
            disabled={isGenerating}
            // 16px on mobile prevents iOS Safari from auto-zooming the page on focus; 15px from sm: up.
            className="block w-full resize-none border-none bg-transparent text-[16px] leading-6 text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-[15px]"
            style={{
              maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
              overflowY: 'auto',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            }}
          />

          <div className="mt-2 flex items-center gap-1.5">
            {isVoiceSupported && (
              <TrackedButton
                type="button"
                variant="ghost"
                buttonName={isListening ? 'AGENT_VOICE_STOP' : 'AGENT_VOICE_START'}
                onClick={handleVoiceToggle}
                aria-label={t('Agent.AriaVoiceover')}
                aria-pressed={isListening}
                title={t('Agent.AriaVoiceover')}
                className={`flex h-7 w-7 items-center justify-center rounded-md p-0 cursor-pointer transition-colors duration-200 ${
                  isListening
                    ? 'bg-red-50 text-red-500 hover:bg-red-100'
                    : 'text-gray-400 hover:bg-[#7c6cf6]/10 hover:text-[#7c6cf6]'
                }`}>
                <Mic size={14} strokeWidth={1.75} className={isListening ? 'animate-pulse' : ''} />
              </TrackedButton>
            )}

            {onOpenJourneys && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <TrackedButton
                    type="button"
                    variant="ghost"
                    buttonName="AGENT_OPEN_JOURNEYS"
                    onClick={onOpenJourneys}
                    aria-label="Journeys"
                    className="inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-medium text-gray-600 cursor-pointer transition-colors duration-200 hover:bg-[#7c6cf6]/10 hover:text-[#7c6cf6]">
                    <Sparkles size={12} strokeWidth={1.75} />
                    Journeys
                  </TrackedButton>
                </TooltipTrigger>
                <TooltipContent>Open guided journeys</TooltipContent>
              </Tooltip>
            )}

            <span className="flex-1" />

            <TrackedButton
              type="button"
              variant="ghost"
              buttonName={isGenerating ? 'AGENT_STOP_GENERATION' : 'AGENT_SEND_MESSAGE'}
              onClick={isGenerating ? handleStop : handleSend}
              disabled={isActionDisabled}
              aria-disabled={isActionDisabled}
              aria-label={isGenerating ? 'Stop' : 'Send'}
              title={isGenerating ? 'Stop' : 'Send'}
              className="flex h-8 w-8 items-center justify-center rounded-lg p-0 text-white shadow-sm cursor-pointer transition-opacity duration-200 hover:opacity-90 hover:bg-[#7c6cf6] disabled:cursor-not-allowed disabled:opacity-30"
              style={{ backgroundColor: '#7c6cf6' }}>
              <AnimatePresence mode="wait" initial={false}>
                {isGenerating ? (
                  <motion.span
                    key="stop"
                    initial={{ scale: 0.4, opacity: 0, rotate: -45 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.4, opacity: 0, rotate: 45, transition: { duration: 0.08 } }}
                    transition={{ duration: AGENT_DURATIONS.hover, ease: AGENT_EASING.outExpo }}
                    className="inline-flex items-center justify-center">
                    <Square size={13} strokeWidth={2.5} className="animate-pulse" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="send"
                    initial={{ scale: 0.4, opacity: 0, rotate: -45 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.4, opacity: 0, rotate: 45, transition: { duration: 0.08 } }}
                    transition={{ duration: AGENT_DURATIONS.hover, ease: AGENT_EASING.outExpo }}
                    className="inline-flex items-center justify-center">
                    <ArrowUp size={14} strokeWidth={2.25} />
                  </motion.span>
                )}
              </AnimatePresence>
            </TrackedButton>
          </div>
        </div>

        <p className="mt-2 text-center text-[11px] text-gray-400">{t('Agent.ComposerDisclaimer')}</p>
      </div>
    </div>
  );
};

export default Composer;
